<template>
  <div
    id="instances-view"
    class="pa-4"
  >
    <portal
      key="trainingProgramInstancesNavigation"
      to="subNavigation"
    >
      <v-tabs
        slider-color="primary"
        align-with-title
      >
        <v-tab :to="{ name: $routeNames.TRAINING_PROGRAMS }">
          {{ $t('sidebar.training_programs.active_programs.label') }}
        </v-tab>
        <v-tab :to="{ name: $routeNames.TRAINING_PROGRAM_INSTANCES }">
          {{ $t('sidebar.training_programs.active_programs.label') }}
        </v-tab>
      </v-tabs>
    </portal>

    <view-page-header :show-team-select="false" />

    <v-row
      no-gutters
      justify="center"
    >
      <v-col xl="6">
        <section class="instances-view__instances mb-6">
          <v-card class="rounded-lg">
            <v-card-title>
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  <div class="subtitle-2">
                    {{ $t('training_programs.activated_programs.header') }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-text-field
                    v-model="instances.filters.search"
                    dense
                    :label="$t('text_input.search.placeholder')"
                    hide-details
                    clearable
                    solo
                    style="width: 300px"
                    @input="fetchInstances()"
                  />
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="tableHeaders"
                :items="instances.data"
                hide-default-footer
                :items-per-page="instances.pagination.per_page"
                :loading="instances.loading"
              >
                <!--<editor-fold desc="ITEM SLOT">-->
                <template v-slot:item="{ item }">
                  <tr
                    class="clickable"
                    @click="manageTrainingProgramInstance(item.id)"
                  >
                    <td>
                      {{ item.name || item.training_program.name }}
                    </td>
                    <td>
                      {{ $dayjs(item.start_date).format('ddd, ll') }}
                    </td>
                    <td>
                      <div v-if="item.last_event">
                        {{ $dayjs(item.last_event.start_at).format('ddd, ll') }}
                      </div>
                      <div
                        v-else
                        class="grey--text text--lighten-2"
                      >
                        N/A
                      </div>
                    </td>
                    <td style="width: 0;">
                      {{ item.event_count }}
                    </td>
                    <td>
                      <v-chip
                        v-if="$dayjs(item.start_date).isAfter($dayjs(), 'day')"
                        small
                        label
                        dark
                        color="grey"
                      >
                        {{ $t("status.training_program.pending") }}
                      </v-chip>
                      <v-chip
                        v-else-if="item.last_event && $dayjs(item.last_event.start_at).isAfter($dayjs(), 'day')"
                        small
                        label
                        dark
                        color="success"
                      >
                        {{ $t("status.training_program.ongoing") }}
                      </v-chip>
                      <v-chip
                        v-else
                        small
                        color="grey lighten-2"
                        label
                      >
                        {{ $t("status.training_program.finished") }}
                      </v-chip>
                    </td>
                  </tr>
                </template>
                <template
                  v-if="instances.loading && instances.data && instances.data.length === 0"
                  v-slot:body
                >
                  <tbody>
                    <tr
                      v-for="i in 10"
                      :key="i"
                    >
                      <td>
                        <v-skeleton-loader
                          type="text"
                          max-width="100px"
                        />
                      </td>
                      <td />
                      <td />
                    </tr>
                  </tbody>
                </template>
              </v-data-table>
              <div
                v-if="instances.pagination && instances.pagination.last_page > 1"
                class="d-flex justify-center align-center py-5"
              >
                <pagination
                  v-model="instances.pagination.current_page"
                  :last-page="instances.pagination.last_page"
                />
              </div>
            </v-card-text>
          </v-card>
        </section>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import ViewPageHeader from '@/components/ViewPageHeader'
import { reactive } from '@vue/composition-api'
import { EventBus } from '@/plugins/bus'
import Pagination from '@/components/Pagination.vue'
import { TrainingProgramApiService } from '@/data/services/TrainingProgramApiService'
import i18n from '@/plugins/i18n'

export default {
  name: 'TrainingProgramInstancesView',
  components: {
    Pagination,
    ViewPageHeader
  },
  setup(props) {
    const tableHeaders = [
      { value: 'training_program.name', text: i18n.t('table_headers.training_programs.training_program'), sortable: true },
      { value: 'start_date', text: i18n.t('table_headers.training_programs.first_event'), sortable: true },
      { value: 'last_event.start_at', text: i18n.t('table_headers.training_programs.last_event'), sortable: true },
      { value: 'event_count', text: i18n.t('table_headers.training_programs.nr_of_events'), sortable: true },
      { text: i18n.t('table_headers.training_programs.status'), sortable: false }
    ]

    const instances = reactive({
      data: [],
      loading: true,
      pagination: {
        current_page: 1,
        last_page: undefined,
        per_page: 25
      },
      filters: {
        search: ''
      }
    })

    const fetchInstances = debounce(async function () {
      try {
        instances.loading = true

        const queryParams = {
          page: instances.pagination.current_page,
          per_page: instances.pagination.per_page,
          search: instances.filters.search
        }

        const resp = await TrainingProgramApiService.getAllInstances(queryParams)

        instances.data = resp.data
        instances.pagination = resp.meta
      } catch (e) {
        instances.data = []
        instances.pagination.current_page = 1
        instances.pagination.last_page = 1
      } finally {
        instances.loading = false
      }
    }, 500)

    return {
      instances,
      fetchInstances,
      tableHeaders
    }
  },
  computed: {
  },
  watch: {
    'instances.pagination.current_page': function (newVal, oldVal) {
      this.fetchInstances()
    }
  },
  created() {
    this.fetchInstances()

    EventBus.$on(this.$events.TRAINING_PROGRAM_INSTANCE_CREATED, () => this.initFetch())
    EventBus.$on(this.$events.TRAINING_PROGRAM_INSTANCE_UPDATED, () => this.initFetch())
    EventBus.$on(this.$events.TRAINING_PROGRAM_INSTANCE_DELETED, () => {
      this.initFetch()
    })
  },
  beforeDestroy() {
    EventBus.$off(this.$events.TRAINING_PROGRAM_INSTANCE_CREATED)
    EventBus.$off(this.$events.TRAINING_PROGRAM_INSTANCE_UPDATED)
    EventBus.$off(this.$events.TRAINING_PROGRAM_INSTANCE_DELETED)
  },
  methods: {
    initFetch() {
      if (this.instances.pagination.current_page !== 1) {
        this.instances.pagination.current_page = 1
      } else {
        this.fetchInstances()
      }
    },
    manageTrainingProgramInstance(instanceId) {
      EventBus.$emit(this.$events.EDIT_TRAINING_PROGRAM_INSTANCE, instanceId)
    }
  }
}
</script>
